/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Synchrony pre-qualify / apply outbound link helpers (INT-48).
 *
 * Mirrors the legacy live-site behavior captured in the rendered storefront:
 *
 * - PDP "See if you prequalify" is a static outbound link (target=_blank) to the
 *   Ashley `/financing` redirect, which mints the DataShare token server-side and
 *   forwards to Synchrony's hosted eapply page. The PDP affordance never calls
 *   DataShare from the browser — it is pure navigation (BROWSE-12).
 *
 * - Checkout eQuickScreen mints a DataShare application token via the
 *   ashley-synchrony Custom API (POST /token) and opens Synchrony's hosted eapply
 *   page directly with that token (the `dsaToken` query parameter).
 *
 * `uniqueId` (site-wide eQuickScreen identifier) and `siteCode` (per-placement Synchrony
 * identifier) are both NON-SECRET — they appear in the rendered live-site URLs. Callers pass
 * them in from the app config (`useConfig().synchrony`), which is sourced from the
 * `PUBLIC__app__synchrony__*` Managed Runtime env vars. These helpers hold no hardcoded
 * identifiers — only the structural host/path, exactly as the legacy resource bundle did.
 */

/** Ashley financing landing host that 302-redirects to Synchrony eapply (mints the dsaToken). */
const FINANCING_BASE_URL = 'https://www.ashleyfurniture.com';
const PDP_PREQUAL_PATH = '/financing/12-month-financing/PREQUAL';

/** Synchrony-hosted DataShare application entry point. */
const SYNCHRONY_ETAIL_HOST = 'https://etail.mysynchrony.com';
const SYNCHRONY_EAPPLY_PATH = '/eapply/eapply.action';

/**
 * Builds the "See if you prequalify" outbound URL (PDP / cart / order-summary placements).
 *
 * Mirrors legacy: a target=_blank link to the Ashley `/financing` redirect, which mints the
 * DataShare token server-side then forwards to the Synchrony eapply page.
 *
 * @param args.uniqueId Site-wide eQuickScreen uniqueId (from `useConfig().synchrony.uniqueId`).
 * @param args.siteCode Per-placement Synchrony siteCode (e.g. `synchrony.pdpPrequalSiteCode`).
 */
export function buildPdpPrequalUrl(args: { uniqueId: string; siteCode: string }): string {
    const params = new URLSearchParams({
        next: 'true',
        uniqueId: args.uniqueId,
        preQual: 'Y',
        siteCode: args.siteCode,
    });
    return `${FINANCING_BASE_URL}${PDP_PREQUAL_PATH}?${params.toString()}`;
}

/**
 * Builds the checkout eQuickScreen eapply URL opened in the Synchrony popup.
 *
 * The `dsaToken` is the DataShare application token minted by the ashley-synchrony
 * Custom API (POST /token).
 *
 * @param args.uniqueId Site-wide eQuickScreen uniqueId (from `useConfig().synchrony.uniqueId`).
 * @param args.siteCode Checkout-placement Synchrony siteCode (`synchrony.checkoutEapplySiteCode`).
 */
export function buildCheckoutEapplyUrl(args: { dsaToken: string; uniqueId: string; siteCode: string }): string {
    const params = new URLSearchParams({
        uniqueId: args.uniqueId,
        siteCode: args.siteCode,
        preQual: 'Y',
        dsaToken: args.dsaToken,
    });
    return `${SYNCHRONY_ETAIL_HOST}${SYNCHRONY_EAPPLY_PATH}?${params.toString()}`;
}
